import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import Typography from "@/components/ui/typography";

import { formatCurrency, formatTooltipValue } from "./ChartFormats";
import { ChartProps } from "./ChartTypes";

interface ChartRendererProps
  extends Pick<
    ChartProps,
    | "type"
    | "data"
    | "categories"
    | "colors"
    | "height"
    | "showLegend"
    | "hideGrid"
    | "formatYAxis"
  > {}

const CustomTooltip = ({ active, payload, label, formatYAxis }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
        <Typography size="l2" className="font-semibold text-gray-900">
          {label}
        </Typography>
        <Typography size="l3" className="text-gray-600 mt-1">
          {formatYAxis
            ? formatTooltipValue(payload[0].value)
            : payload[0].value}
        </Typography>
      </div>
    );
  }
  return null;
};

export const ChartRenderer = ({
  type,
  data,
  categories,
  // Default to SDS palette: purple, blue, green, yellow, red
  colors = ["#4f55e8", "#5ccedf", "#83b300", "#f1d545", "#f56060"],
  height = 300,
  showLegend = true,
  hideGrid = false,
  formatYAxis = false,
}: ChartRendererProps) => {
  const commonAxisProps = {
    axisLine: false,
    tickLine: false,
    tick: { fontSize: 12, fill: "#767677" },
  };

  const xAxisProps = {
    ...commonAxisProps,
    dataKey: "name",
    dy: 10,
  };

  const yAxisProps = {
    ...commonAxisProps,
    width: formatYAxis ? 80 : 40,
    tickFormatter: formatYAxis ? formatCurrency : undefined,
  };

  const tooltipProps = {
    contentStyle: {
      borderRadius: "8px",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
      border: "none",
      padding: "12px",
    },
    formatter: formatYAxis
      ? (value: number) => [formatTooltipValue(value), null]
      : undefined,
  };

  const legendProps = {
    wrapperStyle: { paddingTop: "20px" },
    verticalAlign: "bottom" as const,
    align: "center" as const,
    iconType: "circle" as const,
  };

  const chartMargin = {
    top: 10,
    right: 10,
    left: formatYAxis ? 20 : 0,
    bottom: 0,
  };

  switch (type) {
    case "line":
      return (
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={data} margin={chartMargin}>
            {!hideGrid && (
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#f0f0f0"
                vertical={false}
              />
            )}
            <XAxis {...xAxisProps} />
            <YAxis {...yAxisProps} />
            <Tooltip content={<CustomTooltip formatYAxis={formatYAxis} />} />
            {showLegend && <Legend {...legendProps} />}
            {categories.map((category, index) => (
              <Line
                key={category}
                type="monotone"
                dataKey={category}
                stroke={colors[index % colors.length]}
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 6 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      );

    case "bar":
      return (
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={data} margin={chartMargin}>
            {!hideGrid && (
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#f0f0f0"
                vertical={false}
              />
            )}
            <XAxis {...xAxisProps} />
            <YAxis {...yAxisProps} />
            <Tooltip content={<CustomTooltip formatYAxis={formatYAxis} />} />
            {showLegend && <Legend {...legendProps} />}
            {categories.map((category, index) => (
              <Bar
                key={category}
                dataKey={category}
                fill={colors[index % colors.length]}
                radius={[4, 4, 0, 0]}
                barSize={20}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      );

    case "area":
      return (
        <ResponsiveContainer width="100%" height={height}>
          <AreaChart data={data} margin={chartMargin}>
            {!hideGrid && (
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#f0f0f0"
                vertical={false}
              />
            )}
            <XAxis {...xAxisProps} />
            <YAxis {...yAxisProps} />
            <Tooltip content={<CustomTooltip formatYAxis={formatYAxis} />} />
            {showLegend && <Legend {...legendProps} />}
            {categories.map((category, index) => (
              <Area
                key={category}
                type="monotone"
                dataKey={category}
                stroke={colors[index % colors.length]}
                fill={`${colors[index % colors.length]}33`}
                fillOpacity={0.3}
                strokeWidth={2}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      );

    default:
      return null;
  }
};
